// COP Copilot runtime — the in-process turn driver (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// THE ADAPTATION FOR THE COP CONTEXT (not a rewrite). In takbot the SANDBOXED IFRAME drove the
// loop: it POSTed /agent/turn to the Node runtime, ran each returned tool_call through the parent
// broker over postMessage, then POSTed /agent/continue with the results — repeat until {final}.
// The COP has NO iframe and NO HTTP runtime: the loop runs IN-PROCESS in the browser. This driver
// preserves the EXACT cadence — handleTurn → execute tool_calls → handleContinue → repeat — but
// the "execute each tool_call" step is an injected function that runs the call OVER THE CONFINED
// (PDP-visible) working set, in-process, instead of bouncing through an iframe/broker.
//
// CONFINEMENT (security-critical): `executeToolCall` is the ONLY way a tool runs, and the COP
// capability wires it to execute over the host-filtered working set ONLY (the rows it returns are
// already-cleared previews — see runtimeBridge.ts). The model receives back ONLY the [n]
// tool-RESULT previews context.ts assembles. There is no path here that hands the model a raw
// envelope, a sealed/decrypted payload, or a denied-object count.

import type { AgentStep, FinalAnswer, McpManifest, ToolCall, ToolResult } from "./types.ts";
import { AgentLoop } from "./loop.ts";
import type { Backend } from "./backends/types.ts";

/** Execute ONE tool call over the confined working set, returning the result the loop feeds back
 *  to the model (a preview of already-cleared rows, or a Glomar-safe error). The capability
 *  supplies this — it is the sole tool-execution surface. */
export type ExecuteToolCall = (call: ToolCall) => Promise<ToolResult> | ToolResult;

/** The structured outcome of a completed turn — the final answer plus the trace of which tool
 *  calls ran (so the panel can show real turns + a HWM roll-up). */
export interface TurnOutcome {
  final: FinalAnswer;
  /** The (call, result) pairs that ran this turn, in order — for the panel's result surface. */
  steps: Array<{ call: ToolCall; result: ToolResult }>;
  /** The runtime trace line (backend/model + final marking) for a debug tray. */
  trace?: string;
  /** Total model steps taken (tool-call rounds). */
  toolRounds: number;
}

/** Run a full agent turn in-process: drive the AgentLoop, executing each tool call over the
 *  confined working set between steps, until the loop emits its {final}. A hard iteration cap
 *  backstops the loop's own step cap (defence in depth — the loop always terminates anyway). */
export async function runAgentTurn(
  backend: Backend,
  manifest: McpManifest,
  sessionId: string,
  userText: string,
  executeToolCall: ExecuteToolCall,
  hardCap = 24,
): Promise<TurnOutcome> {
  const loop = new AgentLoop(backend);
  const ranSteps: Array<{ call: ToolCall; result: ToolResult }> = [];
  let toolRounds = 0;

  let step: AgentStep = await loop.handleTurn({ session_id: sessionId, user_text: userText, manifest });

  while (step.status === "tool_calls" && toolRounds < hardCap) {
    toolRounds += 1;
    const calls = step.tool_calls ?? [];
    const results: ToolResult[] = [];
    for (const call of calls) {
      const result = await executeToolCall(call);
      results.push(result);
      ranSteps.push({ call, result });
    }
    step = await loop.handleContinue({ session_id: sessionId, turn_id: step.turn_id, tool_results: results });
  }

  const final: FinalAnswer = step.final ?? {
    text: "The runtime did not produce a final answer within the step budget. This answer is LIMITED.",
    citations: [],
    coverage_warning: "Loop did not converge; treat this as a non-answer, not a definitive negative.",
  };

  return { final, steps: ranSteps, trace: step.trace, toolRounds };
}
